import type { CSSProperties } from 'react'
import { memo, Fragment, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getTrustIndicatorsContent } from './helpers'
import { rem } from 'polished'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import eases from 'styles/tools/eases'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import { GERMAN_LOCALE } from 'i18n/util/locale'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const DEFAULT_SLIDE_COUNT = 2
const GERMAN_SLIDE_COUNT = 3
const SLIDE_DURATION_MS = 4000

const Container = styled.div`
  overflow: hidden;
`

const TrustIndicatorsMobile = styled.div<{ $slideCount: number }>`
  display: grid;
  gap: ${rem(16)};
  overflow: visible;
  grid-template-columns: repeat(${(props) => props.$slideCount}, 100%);
  width: 100%;
  transition: transform 0.5s ${eases.power2.inOut};

  ${mediaQueryUp.desktop} {
    grid-template-columns: unset;
    grid-auto-columns: none;
    grid-auto-flow: column;
  }
`

const TrustIndicatorsDesktop = styled.div`
  display: flex;
  justify-content: space-around;

  &.padding {
    ${mediaQueryUp.desktop} {
      padding: 0 ${rem(100)};
    }
  }
`

function TrustIndicatorSection() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const trustIndicators = useMemo(() => {
    return getTrustIndicatorsContent(currentRegionCode).filter(
      (trustIndicator) => trustIndicator.show,
    )
  }, [currentRegionCode])

  const isGermanLocale = intl.locale === GERMAN_LOCALE
  const slideCount = isGermanLocale ? GERMAN_SLIDE_COUNT : DEFAULT_SLIDE_COUNT

  const mobileSlides = useMemo(() => {
    // German uses 3 slides on mobile because the labels are longer
    if (isGermanLocale) {
      return (
        <>
          <Group
            direction="horizontal"
            horizontalAlign="center"
            verticalAlign="center"
          >
            {trustIndicators[0]?.Component}
          </Group>
          <Group
            direction="horizontal"
            horizontalAlign="center"
            verticalAlign="center"
          >
            {trustIndicators[1]?.Component}
          </Group>
          <Group
            direction="horizontal"
            horizontalAlign="stretch"
            verticalAlign="center"
          >
            {trustIndicators[2]?.Component}
            {trustIndicators[3]?.Component}
          </Group>
        </>
      )
    }

    return (
      <>
        <Group
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="center"
        >
          {trustIndicators[0]?.Component}
          {trustIndicators[1]?.Component}
        </Group>
        <Group
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="center"
        >
          {trustIndicators[2]?.Component}
          {trustIndicators[3]?.Component}
        </Group>
      </>
    )
  }, [isGermanLocale, trustIndicators])

  const isCarouselEnabled = !useScreenSizeUp('desktop')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(
    slideCount,
    SLIDE_DURATION_MS,
    { enabled: isCarouselEnabled },
  )

  const slideStyle: CSSProperties | undefined = isCarouselEnabled
    ? {
        transform: `translateX(calc(${-activeIndex} * (100% + ${rem(16)})))`,
      }
    : undefined

  return (
    <Container>
      <CSSBreakpoint max="tablet">
        <VerticalSpacer gap={20}>
          <TrustIndicatorsMobile $slideCount={slideCount} style={slideStyle}>
            {mobileSlides}
          </TrustIndicatorsMobile>
          <PaginationDots
            count={slideCount}
            activeIndex={activeIndex}
            slideDuration={SLIDE_DURATION_MS}
            onSelect={goToSlide}
            paused={paused}
            onHoverChange={setPaused}
          />
        </VerticalSpacer>
      </CSSBreakpoint>
      <CSSBreakpoint min="desktop">
        <TrustIndicatorsDesktop
          className={clsx({ padding: trustIndicators.length < 3 })}
        >
          {trustIndicators.map(({ Component, key }) => (
            <Fragment key={key}>{Component}</Fragment>
          ))}
        </TrustIndicatorsDesktop>
      </CSSBreakpoint>
    </Container>
  )
}

export default memo(TrustIndicatorSection)
